import apiClient from './apiClient';

export interface Product {
  id: string;
  name: string;
  description?: string;
  category: string;
  price_per_unit: number;
  unit: string;
  minimum_order_quantity: number;
  current_stock: number;
  is_available: boolean;
  is_featured?: boolean;
  seller_name?: string;
  farm_id?: string;
  product_images?: string[];
  quality_grade?: string;
  created_at: string;
  updated_at?: string;
}

export interface FarmSummary {
  id: string;
  name: string;
  location?: string;
  description?: string;
  owner_id?: string;
  coordinates?: { lat?: number; lng?: number } | Record<string, any> | null;
  size_hectares?: number;
  certification_type?: string;
  certification_number?: string;
  is_active?: boolean;
  farm_images?: string[];
  contact_person?: string;
  contact_phone?: string;
}

export interface FarmCreateInput {
  name: string;
  location?: string;
  description?: string;
  coordinates?: { lat?: number; lng?: number } | null;
  size_hectares?: number;
  certification_type?: string;
  certification_number?: string;
  is_active?: boolean;
  farm_images?: string[];
  contact_person?: string;
  contact_phone?: string;
}

export interface ProductCreateInput {
  name: string;
  description?: string;
  category: string;
  unit: string;
  price_per_unit: number;
  current_stock: number;
  minimum_order_quantity: number;
  is_available?: boolean;
  is_featured?: boolean;
  farm_id?: string;
  variety?: string;
  quality_grade?: string;
  storage_conditions?: string;
  product_images?: string[];
}

export interface ProductQueryParams {
  skip?: number;
  limit?: number;
  category?: string;
  farm_id?: string;
  is_available?: boolean;
  featured?: boolean;
  sort_by?: 'created_at' | 'current_stock' | 'price_per_unit';
  sort_order?: 'asc' | 'desc';
  search?: string;
}

const API_BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/products`;

export const fetchProducts = async (
  tenantId: string,
  params: ProductQueryParams = {}
): Promise<Product[]> => {
  const query = new URLSearchParams();
  if (params.skip !== undefined) query.append('skip', params.skip.toString());
  if (params.limit !== undefined) query.append('limit', params.limit.toString());
  if (params.category) query.append('category', params.category);
  if (params.farm_id) query.append('farm_id', params.farm_id);
  if (params.is_available !== undefined) query.append('is_available', String(params.is_available));
  if (params.featured !== undefined) query.append('featured', String(params.featured));
  if (params.sort_by) query.append('sort_by', params.sort_by);
  if (params.sort_order) query.append('sort_order', params.sort_order);
  if (params.search) query.append('search', params.search);

  const url = `${API_BASE}/?${query.toString()}`;
  const headers: Record<string, string> = {};
  if (tenantId) headers['X-Tenant-ID'] = tenantId;

  const response = await apiClient.get(url, { headers });
  return response.data;
};

export const fetchProductById = async (tenantId: string, productId: string): Promise<Product> => {
  const url = `${API_BASE}/${productId}`;
  const headers: Record<string, string> = {};
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  const response = await apiClient.get(url, { headers });
  return response.data;
}; 

export const updateProduct = async (
  tenantId: string,
  productId: string,
  updates: Partial<Product>
): Promise<Product> => {
  const url = `${API_BASE}/${productId}`;
  const headers: Record<string, string> = {};
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  const response = await apiClient.put(url, updates, { headers });
  return response.data;
};

export const createProduct = async (
  tenantId: string,
  payload: ProductCreateInput
): Promise<Product> => {
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  const response = await apiClient.post(`${API_BASE}/`, payload, { headers });
  return response.data;
};

export const deleteProduct = async (tenantId: string, productId: string): Promise<void> => {
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  await apiClient.delete(`${API_BASE}/${productId}`, { headers });
};

export const listFarms = async (tenantId: string): Promise<FarmSummary[]> => {
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  const response = await apiClient.get(`/products/farms/`, { headers });
  return response.data;
};

export const createFarm = async (tenantId: string, payload: FarmCreateInput): Promise<FarmSummary> => {
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  const response = await apiClient.post(`/products/farms/`, payload, { headers });
  return response.data;
};

export const updateFarm = async (
  tenantId: string,
  farmId: string,
  payload: FarmCreateInput
): Promise<FarmSummary> => {
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  const response = await apiClient.put(`/products/farms/${farmId}`, payload, { headers });
  return response.data;
};

export const deleteFarm = async (tenantId: string, farmId: string): Promise<void> => {
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  await apiClient.delete(`/products/farms/${farmId}`, { headers });
};
